import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import api from '../../lib/api';
import { toast } from 'react-toastify';

const HistoriqueSMSTab = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    type_sms: '',
    date_debut: '',
    date_fin: '',
    limit: 100
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Charger statistiques et logs en parallèle
      const [statsRes, logsRes] = await Promise.all([
        api.get('/sms/statistics'),
        api.get('/sms/logs', { params: filters })
      ]);
      
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Erreur chargement données SMS:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    loadData();
  };

  const getTypeBadge = (type) => {
    const styles = {
      'PRIME_VERSEE': 'bg-green-100 text-green-800',
      'CODE_PIN': 'bg-blue-100 text-blue-800',
      'CHANGEMENT_STATUT': 'bg-orange-100 text-orange-800',
      'REINITIALISATION_PIN': 'bg-purple-100 text-purple-800',
      'ANNIVERSAIRE': 'bg-pink-100 text-pink-800'
    };
    
    const labels = {
      'PRIME_VERSEE': '🎁 Prime',
      'CODE_PIN': '🔐 Code PIN',
      'CHANGEMENT_STATUT': '⚠️ Statut',
      'REINITIALISATION_PIN': '🔄 Réinit PIN',
      'ANNIVERSAIRE': '🎂 Anniversaire'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">📊 Historique & Statistiques SMS</h2>
        <p className="text-gray-600 mt-1">Suivi complet de tous les SMS envoyés</p>
      </div>

      {/* Statistiques globales */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Total SMS Envoyés</p>
                <p className="text-3xl font-bold text-violet-600">{stats.total_sms}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Aujourd'hui: {stats.sms_aujourd_hui} | Ce mois: {stats.sms_ce_mois}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Taux de Succès</p>
                <p className="text-3xl font-bold text-green-600">{stats.taux_succes}%</p>
                <p className="text-xs text-gray-500 mt-2">
                  Réussis: {stats.sms_success} | Échoués: {stats.sms_failed}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Coût Total Estimé</p>
                <p className="text-3xl font-bold text-blue-600">${stats.cout_total_estime_usd}</p>
                <p className="text-xs text-gray-500 mt-2">
                  ~{Math.round(stats.cout_total_estime_usd / 0.08)} SMS
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Type le Plus Envoyé</p>
                <p className="text-2xl font-bold text-orange-600">
                  {Object.keys(stats.par_type).length > 0
                    ? Object.entries(stats.par_type).sort((a, b) => b[1] - a[1])[0][0].replace('_', ' ')
                    : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {Object.keys(stats.par_type).length > 0
                    ? `${Object.entries(stats.par_type).sort((a, b) => b[1] - a[1])[0][1]} envois`
                    : ''}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Répartition par type */}
      {stats && Object.keys(stats.par_type).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>📈 Répartition par Type de SMS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-3">
              {Object.entries(stats.par_type).map(([type, count]) => (
                <div key={type} className="bg-gray-50 p-3 rounded-lg text-center">
                  {getTypeBadge(type)}
                  <p className="text-2xl font-bold text-gray-900 mt-2">{count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card>
        <CardHeader>
          <CardTitle>🔍 Filtres</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type SMS</label>
              <select
                value={filters.type_sms}
                onChange={(e) => setFilters({...filters, type_sms: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
              >
                <option value="">Tous les types</option>
                <option value="PRIME_VERSEE">Prime versée</option>
                <option value="CODE_PIN">Code PIN</option>
                <option value="CHANGEMENT_STATUT">Changement statut</option>
                <option value="REINITIALISATION_PIN">Réinitialisation PIN</option>
                <option value="ANNIVERSAIRE">Anniversaire</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
              <input
                type="date"
                value={filters.date_debut}
                onChange={(e) => setFilters({...filters, date_debut: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
              <input
                type="date"
                value={filters.date_fin}
                onChange={(e) => setFilters({...filters, date_fin: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleFilterChange}
                className="w-full px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
              >
                🔍 Filtrer
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historique des SMS */}
      <Card>
        <CardHeader>
          <CardTitle>📱 Historique des SMS ({logs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-500 text-lg">Aucun SMS trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Employé</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Numéro</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Message</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.map((log, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.sent_at)}</td>
                      <td className="px-4 py-3 text-sm">{getTypeBadge(log.type)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{log.employe_nom}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.to_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{log.message}</td>
                      <td className="px-4 py-3 text-center">
                        {log.status === 'success' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✅ Envoyé
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            ❌ Échec
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoriqueSMSTab;
